import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdowns when route changes
  useEffect(() => {
    setIsServicesOpen(false);
    setIsMobileMenuOpen(false);
  }, [location]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsServicesOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      setIsServicesOpen(false);
    }, 200);
  };

  const services = [
    { name: 'All Services', path: '/services' },
    { name: 'Roof Repair', path: '/services/roof-repair' },
    { name: 'Roof Replacement', path: '/services/roof-replacement' },
    { name: 'Roof Inspection', path: '/services/roof-inspection' },
    { name: 'Commercial Roofing', path: '/services/commercial-roofing' },
    { name: 'Insurance Adjuster Help', path: '/services/insurance-adjuster' },
    { name: 'Emergency Roof Repair', path: '/services/emergency-roof-repair' },
  ];

  return (
    <nav
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 sm:h-20">
          <Link to="/" className="flex items-center flex-shrink-0">
            <div className="flex items-center">
              <i className={`ri-home-smile-2-line text-2xl sm:text-3xl ${isScrolled ? 'text-orange-600' : 'text-white'}`}></i>
              <span className={`ml-2 sm:ml-3 text-lg sm:text-2xl font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
                NWA ROOFING
              </span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6 xl:space-x-8">
            <Link
              to="/"
              className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-blue-200'
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <div 
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`font-medium transition-colors whitespace-nowrap flex items-center text-sm xl:text-base ${
                  isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-blue-200'
                }`}
              >
                Services
                <i className="ri-arrow-down-s-line ml-1"></i>
              </button>

              {/* Dropdown Menu */}
              {isServicesOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white rounded-lg shadow-2xl py-2 border border-gray-100">
                  {services.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block px-6 py-3 text-sm text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors"
                    >
                      {service.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/about"
              className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-blue-200'
              }`}
            >
              About
            </Link>
            <Link
              to="/blog"
              className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-blue-200'
              }`}
            >
              Blog
            </Link>
            <Link
              to="/contact"
              className={`font-medium transition-colors whitespace-nowrap text-sm xl:text-base ${
                isScrolled ? 'text-gray-700 hover:text-orange-600' : 'text-white hover:text-blue-200'
              }`}
            >
              Contact
            </Link>
            <a
              href="tel:4793455312"
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 xl:px-6 py-2.5 xl:py-3 rounded-lg text-sm xl:text-base font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg whitespace-nowrap"
            >
              (479) 345-5312
            </a>
          </div>

          {/* Mobile Menu Button - Touch-friendly 48x48px */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden min-w-[48px] min-h-[48px] flex items-center justify-center touch-manipulation ${
              isScrolled ? 'text-gray-900' : 'text-white'
            }`}
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-3xl`}></i>
          </button>
        </div>

        {/* Mobile Menu - Full Screen Overlay */}
        {isMobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 top-16 sm:top-20 bg-white z-50 overflow-y-auto">
            <div className="py-4">
              <Link
                to="/"
                className="block px-6 py-4 text-base font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors active:bg-orange-100 touch-manipulation"
              >
                Home
              </Link>

              {/* Mobile Services Dropdown */}
              <div>
                <button
                  onClick={() => setIsServicesOpen(!isServicesOpen)}
                  className="w-full flex items-center justify-between px-6 py-4 text-base font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors active:bg-orange-100 touch-manipulation"
                  aria-expanded={isServicesOpen}
                >
                  <span>Services</span>
                  <i className={`ri-arrow-${isServicesOpen ? 'up' : 'down'}-s-line text-xl`}></i>
                </button>
                {isServicesOpen && (
                  <div className="bg-gray-50">
                    {services.map((service, index) => (
                      <Link
                        key={index}
                        to={service.path}
                        className="block px-10 py-3 text-sm text-gray-600 hover:text-orange-600 transition-colors active:bg-orange-50 touch-manipulation"
                      >
                        {service.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              <Link
                to="/about"
                className="block px-6 py-4 text-base font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors active:bg-orange-100 touch-manipulation"
              >
                About
              </Link>
              <Link
                to="/blog"
                className="block px-6 py-4 text-base font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors active:bg-orange-100 touch-manipulation"
              >
                Blog
              </Link>
              <Link
                to="/contact"
                className="block px-6 py-4 text-base font-medium text-gray-700 hover:bg-orange-50 hover:text-orange-600 transition-colors active:bg-orange-100 touch-manipulation"
              >
                Contact
              </Link>
              
              {/* Mobile CTA Button - Touch-friendly */}
              <div className="px-6 py-4">
                <a
                  href="tel:4793455312"
                  className="block text-center bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-4 rounded-lg text-base font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg whitespace-nowrap touch-manipulation active:scale-95"
                >
                  <i className="ri-phone-fill mr-2"></i>
                  Call (479) 345-5312
                </a>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;